import {
  FLOW_ID_PATTERN,
  IDENTITY_PROXY_PATH,
  SESSION_AALS,
  boundedTimestamp,
  invalid,
  isRecord,
  optionalString,
  requiredString,
  validateExactKeys,
} from "./kratos-contract-primitives.js";
import {
  decodeAccountAvatarColor,
  decodeAccountAvatarReference,
  decodeDiscordAvatarUrl,
} from "./avatar-reference.js";
import { accountRouteForPathname } from "./account-route-contract.js";
import {
  MAX_ACCOUNT_EMAIL_ADDRESSES,
  decodeAccountEmailAddresses,
} from "./email-address-contract.js";

const IDENTITY_SCHEMA_ID = "fishystuff-v1";
const MAX_OTHER_SESSIONS = 32;
const MAX_DISPLAY_NAME_SCALARS = 80;
const MAX_PASSKEY_LABELS = 10;
const MAX_PASSKEY_LABEL_UTF16_UNITS = 80;
const PASSKEY_CREDENTIAL_ID_PATTERN = /^(?:[0-9a-f]{2}){1,512}$/;

function missingPasskeyLabels() {
  return Object.freeze({ kind: "missing", schemaVersion: 1 });
}

export function decodePasskeyLabelsProjectionV1(value) {
  if (!isRecord(value) || value.schemaVersion !== 1) {
    invalid("Session passkey labels are invalid");
  }
  if (value.kind === "missing") {
    validateExactKeys(value, new Set(["kind", "schemaVersion"]), "Session passkey labels");
    return missingPasskeyLabels();
  }
  validateExactKeys(
    value,
    new Set(["kind", "schemaVersion", "revision", "labels"]),
    "Session passkey labels",
  );
  if (
    value.kind !== "ready"
    || !Number.isSafeInteger(value.revision)
    || value.revision < 1
    || !isRecord(value.labels)
  ) {
    invalid("Session passkey labels are invalid");
  }
  const entries = Object.entries(value.labels);
  if (entries.length > MAX_PASSKEY_LABELS) {
    invalid("Session passkey labels exceed their item limit");
  }
  const labels = {};
  for (const [credentialId, label] of entries) {
    if (
      !PASSKEY_CREDENTIAL_ID_PATTERN.test(credentialId)
      || typeof label !== "string"
      || label.length < 1
      || label.length > MAX_PASSKEY_LABEL_UTF16_UNITS
      || label.trim() !== label
      || label.includes("\0")
    ) {
      invalid("Session passkey label is invalid");
    }
    labels[credentialId] = label;
  }
  return Object.freeze({
    kind: "ready",
    schemaVersion: 1,
    revision: value.revision,
    labels: Object.freeze(labels),
  });
}

function boundedDisplayName(value) {
  if (value === null) invalid("Session display name is invalid");
  const displayName = optionalString(value, "Session display name", {
    maxLength: MAX_DISPLAY_NAME_SCALARS * 2,
  });
  if ([...displayName].length > MAX_DISPLAY_NAME_SCALARS) {
    invalid("Session display name is invalid");
  }
  return displayName;
}

function decodeSessionMetadata(value, label) {
  if (!isRecord(value) || value.active !== true) {
    invalid(`${label} is not active`);
  }
  const id = requiredString(value.id, `${label} ID`, { maxLength: 64 });
  if (!FLOW_ID_PATTERN.test(id)) {
    invalid(`${label} ID is invalid`);
  }
  const expiresAt = boundedTimestamp(value.expires_at, `${label} expiry`);
  const authenticatedAt = boundedTimestamp(
    value.authenticated_at,
    `${label} authentication time`,
  );
  const aal = requiredString(value.authenticator_assurance_level, `${label} AAL`, {
    maxLength: 4,
  });
  if (!SESSION_AALS.has(aal)) {
    invalid(`${label} AAL is unsupported`, "unsupported");
  }
  return Object.freeze({ id, expiresAt, authenticatedAt, aal });
}

function emailVerificationStatus(address) {
  if (!isRecord(address) || address.via !== "email") {
    invalid("Session email verification projection is invalid");
  }
  if (typeof address.verified !== "boolean") {
    invalid("Session email verification flag is invalid");
  }
  const status = requiredString(address.status, "Session email verification status", {
    maxLength: 16,
  });
  if (address.verified) {
    if (status !== "completed") {
      invalid("Verified session email has an inconsistent status");
    }
    return "verified";
  }
  if (!["pending", "sent"].includes(status)) {
    invalid("Unverified session email has an inconsistent status");
  }
  return "pending";
}

function decodeEmailAddresses(identity, configured) {
  // Kratos omits an empty verifiable-address collection from its public
  // identity JSON. An identity that carries an email must provide the derived
  // address projection so the two authoritative Kratos views cannot disagree.
  if (!Object.hasOwn(identity, "verifiable_addresses")) {
    if (configured.length > 0) {
      invalid("Session email verification projection is invalid");
    }
    return Object.freeze([]);
  }
  const addresses = identity.verifiable_addresses;
  if (!Array.isArray(addresses) || addresses.length > MAX_ACCOUNT_EMAIL_ADDRESSES) {
    invalid("Session email verification projection is invalid");
  }
  if (configured.length === 0) {
    if (addresses.length > 0) {
      invalid("Session has a verification address without an email trait", "unsupported");
    }
    return Object.freeze([]);
  }
  if (addresses.length !== configured.length) {
    invalid("Session email verification projection is incomplete");
  }
  const addressByValue = new Map();
  for (const address of addresses) {
    if (!isRecord(address) || typeof address.value !== "string") {
      invalid("Session email verification projection is invalid");
    }
    if (addressByValue.has(address.value)) {
      invalid("Session email verification projection is ambiguous", "unsupported");
    }
    addressByValue.set(address.value, address);
  }
  return Object.freeze(configured.map((value) => {
    const address = addressByValue.get(value);
    if (!address) {
      invalid("Session email verification projection does not match the email traits");
    }
    return Object.freeze({
      value,
      verification: emailVerificationStatus(address),
    });
  }));
}

function configuredEmailAddresses(value) {
  try {
    return decodeAccountEmailAddresses(
      value === undefined ? [] : value,
      "Session email addresses",
    );
  } catch (error) {
    invalid(error instanceof Error ? error.message : "Session email addresses are invalid");
  }
}

export function decodeActiveSession(value) {
  const metadata = decodeSessionMetadata(value, "Identity session");
  if (
    !isRecord(value.identity)
    || value.identity.schema_id !== IDENTITY_SCHEMA_ID
    || value.identity.state !== "active"
    || !isRecord(value.identity.traits)
  ) {
    invalid("Identity session is not an active FishyStuff session");
  }
  const traitKeys = Object.keys(value.identity.traits);
  if (traitKeys.some((key) => ![
    "username",
    "display_name",
    "emails",
    "avatar_url",
    "avatar_color",
  ].includes(key))) {
    invalid("Identity session contains an unsupported trait", "unsupported");
  }
  const username = requiredString(value.identity.traits.username, "Session username", {
    maxLength: 32,
  });
  // Preserve trait absence here; presentation owners may show the handle as a
  // fallback without turning that fallback into identity data.
  const hasDisplayName = Object.hasOwn(value.identity.traits, "display_name");
  const displayName = hasDisplayName
    ? boundedDisplayName(value.identity.traits.display_name)
    : "";
  const configuredEmails = configuredEmailAddresses(value.identity.traits.emails);
  let avatarUrl = "";
  let avatarColor = "base";
  let discordAvatarUrl = "";
  let passkeyLabels = missingPasskeyLabels();
  const warnings = [];
  try {
    avatarUrl = decodeAccountAvatarReference(
      value.identity.traits.avatar_url,
      "Session avatar",
    );
  } catch (error) {
    warnings.push(Object.freeze({
      kind: "invalidAvatar",
      message: error instanceof Error ? error.message : "Session avatar is invalid",
    }));
  }
  try {
    avatarColor = decodeAccountAvatarColor(
      value.identity.traits.avatar_color,
      "Session avatar color",
    );
  } catch (error) {
    warnings.push(Object.freeze({
      kind: "invalidAvatarColor",
      message: error instanceof Error ? error.message : "Session avatar color is invalid",
    }));
  }
  try {
    if (
      Object.hasOwn(value.identity, "metadata_public")
      && value.identity.metadata_public !== null
    ) {
      validateExactKeys(
        value.identity.metadata_public,
        new Set([
          "discord_avatar_url",
          "discord_handle",
          "fishystuff_legal_acceptance",
          "passkey_labels",
        ]),
        "Session public identity metadata",
      );
      discordAvatarUrl = decodeDiscordAvatarUrl(
        value.identity.metadata_public.discord_avatar_url,
        "Session Discord avatar",
      );
    } else {
      discordAvatarUrl = "";
    }
  } catch (error) {
    warnings.push(Object.freeze({
      kind: "invalidDiscordAvatar",
      message: error instanceof Error
        ? error.message
        : "Session Discord avatar is invalid",
    }));
  }
  try {
    const stored = value.identity.metadata_public?.passkey_labels;
    if (stored !== undefined) {
      passkeyLabels = decodePasskeyLabelsProjectionV1(stored);
      if (passkeyLabels.kind !== "ready") {
        invalid("Stored session passkey labels are invalid");
      }
    }
  } catch (error) {
    passkeyLabels = missingPasskeyLabels();
    warnings.push(Object.freeze({
      kind: "invalidPasskeyLabels",
      message: error instanceof Error
        ? error.message
        : "Session passkey labels are invalid",
    }));
  }
  return Object.freeze({
    kind: "authenticated",
    ...metadata,
    traits: Object.freeze({
      username,
      displayName,
      emails: configuredEmails,
      avatarUrl,
      avatarColor,
    }),
    discordAvatarUrl,
    passkeyLabels,
    emailAddresses: decodeEmailAddresses(value.identity, configuredEmails),
    warnings: Object.freeze(warnings),
  });
}

export function decodeOtherSessions(value) {
  if (!Array.isArray(value) || value.length > MAX_OTHER_SESSIONS) {
    invalid("Other identity sessions are invalid");
  }
  const ids = new Set();
  const sessions = value.map((session, index) => {
    const decoded = decodeSessionMetadata(session, `Other identity session ${index}`);
    if (ids.has(decoded.id)) {
      invalid("Other identity session ID appears more than once");
    }
    ids.add(decoded.id);
    return decoded;
  });
  return Object.freeze(sessions);
}

export function decodeOtherSessionsRevocation(value) {
  validateExactKeys(value, new Set(["count"]), "Other-session revocation");
  if (!Number.isSafeInteger(value.count) || value.count < 0 || value.count > MAX_OTHER_SESSIONS) {
    invalid("Other-session revocation count is invalid");
  }
  return Object.freeze({ count: value.count });
}

function exactApiOrigin(context) {
  const apiOrigin = requiredString(context.apiOrigin, "API origin", { maxLength: 2048 });
  let url;
  try {
    url = new URL(apiOrigin);
  } catch (_error) {
    invalid("API origin is invalid");
  }
  if (
    !["http:", "https:"].includes(url.protocol)
    || url.origin !== apiOrigin
    || url.pathname !== "/"
    || url.search
    || url.hash
    || url.username
    || url.password
  ) {
    invalid("API origin is invalid");
  }
  return apiOrigin;
}

function exactIdentityBaseUrl(context) {
  const apiOrigin = exactApiOrigin(context);
  const value = requiredString(context.identityBaseUrl, "Identity browser base URL", {
    maxLength: 2048,
  });
  let url;
  try {
    url = new URL(value);
  } catch (_error) {
    invalid("Identity browser base URL is invalid");
  }
  const sharedAuthority = value === "https://id.fishystuff.fish/identity"
    && (
      context.pageHostname === "fishystuff.fish"
      || context.pageHostname?.endsWith(".fishystuff.fish")
    );
  if (
    (value !== `${apiOrigin}${IDENTITY_PROXY_PATH}` && !sharedAuthority)
    || url.pathname !== IDENTITY_PROXY_PATH
    || url.search
    || url.hash
    || url.username
    || url.password
    || context.identityOrigin !== url.origin
  ) {
    invalid("Identity browser base URL is outside the exact account contract");
  }
  return value;
}

function exactSessionCollectionUrl(context) {
  if (!isRecord(context)) {
    invalid("Other-session context is invalid");
  }
  const expected = `${exactIdentityBaseUrl(context)}/sessions`;
  if (context.sessionsUrl !== expected) {
    invalid("Session collection URL is outside the identity authority");
  }
  return expected;
}

export function otherSessionRevokeUrl(context, sessionId) {
  const collectionUrl = exactSessionCollectionUrl(context);
  const id = requiredString(sessionId, "Other session ID", { maxLength: 64 });
  if (!FLOW_ID_PATTERN.test(id)) {
    invalid("Other session ID is invalid");
  }
  return `${collectionUrl}/${id}`;
}

function exactAccountReturnUrl(context) {
  if (!isRecord(context)) {
    invalid("Identity session context is invalid");
  }
  const returnUrl = requiredString(context.returnUrl, "Localized account return URL", {
    maxLength: 2048,
  });
  let parsedReturnUrl;
  try {
    parsedReturnUrl = new URL(returnUrl);
  } catch (_error) {
    invalid("Localized account return URL is invalid");
  }
  const pageOrigin = requiredString(context.pageOrigin, "Account page origin", {
    maxLength: 2048,
  });
  if (
    !["http:", "https:"].includes(parsedReturnUrl.protocol)
    || parsedReturnUrl.username
    || parsedReturnUrl.password
    || parsedReturnUrl.origin !== pageOrigin
    || parsedReturnUrl.search
    || parsedReturnUrl.hash
    || !accountRouteForPathname(parsedReturnUrl.pathname)
  ) {
    invalid("Localized account return URL is invalid");
  }
  return returnUrl;
}

function exactStepUpUrl(context) {
  const identityBaseUrl = exactIdentityBaseUrl(context);
  const returnUrl = exactAccountReturnUrl(context);
  const expected = `${identityBaseUrl}/self-service/login/browser?${
    new URLSearchParams({ aal: "aal2", return_to: returnUrl })
  }`;
  if (context.stepUpLoginStartUrl !== expected) {
    invalid("Localized identity step-up URL is invalid");
  }
  return expected;
}

function exactRefreshUrl(context) {
  const identityBaseUrl = exactIdentityBaseUrl(context);
  const returnUrl = exactAccountReturnUrl(context);
  const expected = `${identityBaseUrl}/self-service/login/browser?${
    new URLSearchParams({ refresh: "true", return_to: returnUrl })
  }`;
  if (context.refreshLoginStartUrl !== expected) {
    invalid("Localized identity refresh URL is invalid");
  }
  return expected;
}

function exactSettingsAction(value, context) {
  let action;
  try {
    action = new URL(requiredString(value, "Settings refresh return URL", {
      maxLength: 2048,
    }));
  } catch (_error) {
    invalid("Settings refresh return URL is invalid");
  }
  const identityBaseUrl = new URL(exactIdentityBaseUrl(context));
  const entries = [...action.searchParams.entries()];
  if (
    action.origin !== identityBaseUrl.origin
    || action.pathname !== `${identityBaseUrl.pathname}/self-service/settings`
    || action.username
    || action.password
    || action.hash
    || entries.length !== 1
    || entries[0][0] !== "flow"
    || !FLOW_ID_PATTERN.test(entries[0][1])
  ) {
    invalid("Settings refresh return URL is outside the exact account contract");
  }
  return action.toString();
}

export function decodeSessionAlreadyAvailable(value) {
  validateExactKeys(value, new Set(["error"]), "Existing identity session");
  validateExactKeys(value.error, new Set(["id", "code", "status", "reason", "message"]), "Existing identity session error");
  if (value.error.id !== "session_already_available"
    || value.error.code !== 400 || value.error.status !== "Bad Request") {
    invalid("Identity login initialization error is unsupported", "unsupported");
  }
  requiredString(value.error.message, "Existing identity session message", { maxLength: 1000 });
  requiredString(value.error.reason, "Existing identity session reason", { maxLength: 1000 });
  return Object.freeze({ kind: "sessionAlreadyAvailable" });
}

export function decodeSessionError(value, context, { refreshReturnUrl = "" } = {}) {
  if (!isRecord(value) || !isRecord(value.error)) {
    invalid("Identity session error is invalid");
  }
  if (
    value.error.code !== 403
    || value.error.status !== "Forbidden"
  ) {
    invalid("Identity session error is unsupported", "unsupported");
  }
  optionalString(value.error.message, "Identity session error message", { maxLength: 1000 });
  optionalString(value.error.reason, "Identity session error reason", { maxLength: 1000 });
  let redirect;
  try {
    redirect = new URL(requiredString(
      value.redirect_browser_to,
      "Identity step-up redirect",
      { maxLength: 2048 },
    ));
  } catch (_error) {
    invalid("Identity reauthentication redirect is invalid");
  }
  const identityBaseUrl = new URL(exactIdentityBaseUrl(context));
  const entries = [...redirect.searchParams.entries()];
  const baseIsExact = redirect.origin === identityBaseUrl.origin
    && redirect.pathname === `${identityBaseUrl.pathname}/self-service/login/browser`
    && !redirect.username
    && !redirect.password
    && !redirect.hash;

  if (value.error.id === "session_aal2_required") {
    const aal = entries.filter(([name]) => name === "aal");
    const returnTo = entries.filter(([name]) => name === "return_to");
    if (
      !baseIsExact
      || entries.some(([name]) => !["aal", "return_to"].includes(name))
      || aal.length !== 1
      || aal[0][1] !== "aal2"
      || returnTo.length > 1
      || (returnTo.length === 1 && returnTo[0][1] !== context.returnUrl)
    ) {
      invalid("Identity step-up redirect is outside the exact account contract");
    }
    return Object.freeze({
      mode: "aal2",
      redirectUrl: exactStepUpUrl(context),
    });
  }

  if (value.error.id === "session_refresh_required") {
    const expectedReturnUrl = exactSettingsAction(refreshReturnUrl, context);
    if (
      !baseIsExact
      || entries.length !== 2
      || entries[0][0] !== "refresh"
      || entries[0][1] !== "true"
      || entries[1][0] !== "return_to"
      || entries[1][1] !== expectedReturnUrl
    ) {
      invalid("Identity refresh redirect is outside the exact account contract");
    }
    return Object.freeze({
      mode: "refresh",
      redirectUrl: exactRefreshUrl(context),
    });
  }

  invalid("Identity session error is unsupported", "unsupported");
}
